/* eslint-disable no-template-curly-in-string */
import React from 'react'
import update from 'immutability-helper'
import _ from 'lodash'
import { Table, Column, Cell, ColumnCellProps } from 'fixed-data-table-2'
// @ts-ignore no types
import ReactResizeDetector from 'react-resize-detector'
import classnames from 'classnames/bind'
import {
  CheckboxField,
  Dropdown,
  Icon,
  SearchField as CoreSearchField,
  withApplicationNotificationsContext,
  hooks,
} from '@adc/ui-core'
import type { DropdownItemProps } from 'semantic-ui-react'
import pluralize from 'pluralize'
import {
  DEBOUNCE_WAIT_FOR_SORT,
  DEFAULT_PRIMARY_BATCH_SIZE,
} from './constants/statics'
import { COLUMN_DEFS_PREFIX } from './constants/cookieKeys'
import { formatNumber } from './utils/formatters'
import CheckboxCell from './cells/checkboxCell'
import ActionCell from './cells/actionCell'
import BoolCell from './cells/boolCell'
import CurrencyCell from './cells/currencyCell'
import DateCell from './cells/dateCell'
import FilterCell from './cells/filterCell'
import HeaderCell from './cells/headerCell'
import HtmlCell from './cells/htmlCell'
import LabelCell from './cells/labelCell'
import NumberCell from './cells/numberCell'
import PlaceholderCell from './cells/placeholderCell'
import StatusCell from './cells/statusCell'
import TextCell from './cells/textCell'
import TimeCell from './cells/timeCell'
import ValueToTextCell from './cells/valueToTextCell'
import ClauseHolder from './clauseHolder'
import SavedSearches from './savedSearches'
import SearchField from './searchField'
import DataTableUtils from './utils'
import BulkProcessingModule from './bulkProcessingModule'
import BulkUpdateDrawer from './bulkUpdateDrawer'
import ActionIcon from './actionIcon'
import EditDataColumnsModal, {
  EditDataColumnsMenuItem,
} from './menuOptions/editDataColumns'
import DownloadDataModal, { DownloadMenuItem } from './menuOptions/downloadData'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  COLUMN_STANDARD_SIZES,
} from './constants'
import { DatatableColumnDefinition } from './types'
import defineTableSchema from './tableSchema'
import 'fixed-data-table-2/dist/fixed-data-table.css'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const DEFAULT_ROW_HEIGHT = 35
const DEFAULT_HEADER_HEIGHT = DEFAULT_ROW_HEIGHT

const TOOLBAR_DEFAULT_HEIGHT = 48
const INFOBAR_DEFAULT_HEIGHT = 60 // includes some 8px padding.
const searchRegExp = (searchFilter: string) =>
  new RegExp(searchFilter.replace(/\s/g, '.*'), 'ig')

// Type for column types - extracts the values from COLUMN_TYPES constant
type ColumnType = (typeof COLUMN_TYPES)[keyof typeof COLUMN_TYPES]

// Type for flexible cell/header renderers
type FunctionalElement<T> =
  | string
  | React.ReactElement
  | ((props: T) => string | React.ReactElement)

/**
 * Base type for row data items in the DataTable
 * Extend this interface for specific implementations to get type safety
 *
 * @example
 * interface MyRowData extends DataTableRowData {
 *   user_id: string
 *   email: string
 *   name: string
 * }
 */
interface DataTableRowData {
  // Dynamic properties - each row can have any field based on the data source
  [key: string]: unknown
  // Optional properties used by the DataTable itself
  selected?: boolean // Checkbox selection state
  error?: boolean | string // Error state for row highlighting
  warning?: boolean | string // Warning state for row highlighting
}

// Legacy column format (used in existing apps)
interface LegacyColumnDefinition<
  TRowData extends DataTableRowData = DataTableRowData
> {
  field: string
  type?: ColumnType
  title?: string
  Header?: FunctionalElement<any>
  Cell?: FunctionalElement<any>
  accessor?: (rowData: TRowData) => string | number | null | unknown
  sortField?: string
  dataType?: string
  colProps: {
    fixed?: boolean
    fixedRight?: boolean
    flexGrow?: number
    width?: number
    isResizable?: boolean
    isReorderable?: boolean
    isVisible?: boolean
    searchable?: boolean
    sort?: number
    sortAscending?: boolean
    [key: string]: any
  }
  [key: string]: any
}

// Union type supporting both modern and legacy column formats
// Extended with type property to ensure it's available on all column definitions
type DataTableColumn<TRowData extends DataTableRowData = DataTableRowData> = (
  | DatatableColumnDefinition
  | LegacyColumnDefinition<TRowData>
) & {
  type?: ColumnType
}

interface CustomMenuOption extends Omit<DropdownItemProps, 'onClick'> {
  clickHandler?: () => void
  isDisabled?: boolean
}

interface DownloadData {
  data?: unknown[]
  isLoading?: boolean
  error?: Error | null
  [key: string]: unknown
}

interface DownloadActions {
  fetchData: () => void | Promise<void>
  abort: () => void
  reset: () => void
  exportData?: (data: unknown[]) => void
}

interface SavedSearchesState {
  selectedSearchId?: string | null
  isDirty?: boolean
  searches?: unknown[]
  [key: string]: unknown
}

interface SavedSearchesActions {
  setSelectedSavedSearch: (id: string | null) => void
  setSavedSearchDirty: (isDirty: boolean) => void
  setSavedSearchDirtyColumns: (columns: unknown[]) => void
  fetchSavedSearches: () => void | Promise<void>
  putSavedSearch: () => void | Promise<void>
  postSavedSearch: (
    searchName: string,
    searchType: string
  ) => void | Promise<void>
  deleteSavedSearch: (id: string) => void | Promise<void>
}

interface Suggestions {
  [key: string]: unknown[]
}

interface Sizes {
  rowHeight?: number
  headerHeight?: number
  footerHeight?: number
}

interface OnKeyboardNavigation {
  gotoNextItem: () => void
  gotoPreviousItem: () => void
}

interface FilterClause {
  values: string[]
  fields: string[]
  title: string
  valuesAsText: string
}

interface BrowserStorage {
  getValue: (key: string) => unknown
  setValue: (key: string, value: unknown) => void
  removeValue?: (key: string) => void
}

interface StandardToolbarProps {
  allowExportCSV: boolean
  refreshData: () => void
  isLoading: boolean
  enableLocalSearch: boolean
  enableSavedSearches: boolean
  savedSearches: SavedSearchesState
  filterClauses: FilterClause[]
  userAuthorities: string[]
  renderStandardSearch: () => React.ReactElement
  renderLocalSearch: () => React.ReactElement
  renderSavedSearch: () => React.ReactElement
  renderDataTableActions: () => React.ReactElement
}

interface DataTableProps<TRowData extends DataTableRowData = DataTableRowData> {
  // Required props
  rowData: TRowData[]
  columns: DataTableColumn<TRowData>[] // Array of column definitions
  entityKey: string // e.g. 'VENUE_TEMPLATES'
  primaryKeyField: keyof TRowData // e.g. 'venue_template_id'

  // Optional storage and data management
  browserStorage?: BrowserStorage

  // Data loading and refresh
  isLoading?: boolean
  isNonPrimaryBatchLoading?: boolean
  totalCount?: number
  entityName?: string

  // Sorting and filtering
  changeSortBy?: (
    sortValues: unknown[],
    columns: DataTableColumn<TRowData>[]
  ) => void | Promise<void>
  fetchDataRowsAppend?: () => void | Promise<void>
  refreshData?: () => void
  filterClauses?: FilterClause[]
  addFilter?: (clause: FilterClause) => void
  removeFilter?: (clause: FilterClause) => void
  clearFilters?: () => void

  // Row interactions
  onRowAction?: (action: string, rowData: unknown) => void
  onRowClick?: (
    event: React.MouseEvent,
    rowIndex: number,
    dataIndex: number,
    rowDataOverride?: TRowData // Optional parameter to provide row data directly, need for cases where we may do local text filtering and the index of the local filtered rows doesn't match the original data here.
  ) => void
  onRowDoubleClick?: (
    event: React.MouseEvent,
    rowIndex: number,
    dataIndex: number,
    rowDataOverride?: TRowData // See onRowClick — same reasoning for passing row data directly.
  ) => void
  onScrollEnd?: () => void
  rowAttributesGetter?: (rowIndex: number) => Record<string, unknown>
  activeRowIndex?: number | undefined

  // Row expansion
  collapsedRows?: Set<number>
  rowExpanded?: (props: { rowIndex: number }) => React.ReactElement | string
  subRowHeightGetter?: (rowIndex: number) => number
  handleCollapseClick?: (rowIndex: number) => void

  // Header and toolbar customization
  customTableHeader?: () => React.ReactNode
  hideTableHeader?: boolean
  customToolbar?: (props: StandardToolbarProps) => React.ReactNode
  hideToolbar?: boolean
  customTableFooter?: React.ComponentType<unknown>

  // Empty state messaging
  emptyResultsTitle?: string
  emptyResultsMessage?: string

  // Search and suggestions
  suggestions?: Suggestions
  userAuthorities?: string[] // e.g. ['GSW'] for Global Search Writer
  updateSuggestions?: (suggestions: Suggestions) => void
  minCharsForAsyncSuggestions?: number
  enableLocalSearch?: boolean
  localSearchMatcher?: (row: TRowData, searchText: string) => boolean

  // Export functionality
  allowExportCSV?: boolean
  downloadData?: DownloadData
  downloadActions?: DownloadActions

  // Saved searches
  enableSavedSearches?: boolean
  savedSearches?: SavedSearchesState
  savedSearchesActions?: SavedSearchesActions

  // Visual and behavioral options
  highlightErrorsAndWarnings?: boolean
  sizes?: Sizes
  enableOperationSelection?: boolean

  // Custom formatters and options
  customFormatters?: Record<string, (value: unknown) => string>
  customMenuOptions?: CustomMenuOption[]

  // Bulk processing
  bulkProcessingModules?: BulkProcessingModule[]

  // Keyboard navigation
  onKeyboardNavigation?: OnKeyboardNavigation

  // Notifications context (from withApplicationNotificationsContext HOC)
  notifications?: {
    toast?: {
      open: (config: { title: string; message?: string; role?: string }) => void
    }
  }
}

/**
 * FunctionalDataTable - A generic, fully-featured data table component
 *
 * @template TRowData - The type of data for each row in the table. Should extend DataTableRowData.
 *
 * @example
 * // Define your row data type
 * interface UserRow extends DataTableRowData {
 *   user_id: string
 *   email: string
 *   name: string
 *   role: 'admin' | 'user'
 *   created_at: Date
 * }
 *
 * // Use with type parameter
 * <FunctionalDataTable<UserRow>
 *   rowData={users}
 *   columns={userColumns}
 *   entityKey="USERS"
 *   primaryKeyField="user_id"
 *   changeSortBy={handleSort}
 *   onRowClick={handleRowClick}
 * />
 *
 * @example
 * // Without explicit type (falls back to base DataTableRowData)
 * <FunctionalDataTable
 *   rowData={genericData}
 *   columns={columns}
 *   entityKey="DATA"
 *   primaryKeyField="id"
 * />
 */
const FunctionalDataTable = <
  TRowData extends DataTableRowData = DataTableRowData
>({
  // Required props (no defaults)
  rowData = [],
  columns,
  entityKey,
  primaryKeyField,

  // Optional storage and data management
  browserStorage,

  // Data loading and refresh
  isLoading = false,
  isNonPrimaryBatchLoading,
  totalCount = 0,
  entityName,

  // Sorting and filtering
  changeSortBy = _.noop,
  fetchDataRowsAppend = _.noop,
  refreshData = _.noop,
  filterClauses = [],
  addFilter = _.noop,
  removeFilter = _.noop,
  clearFilters = _.noop,

  // Row interactions
  onRowAction = _.noop,
  onRowClick,
  onRowDoubleClick,
  onScrollEnd = _.noop,
  rowAttributesGetter = () => ({}),
  activeRowIndex = undefined,

  // Row expansion
  collapsedRows = new Set([]),
  // @ts-ignore old default was noop so even though type doesn't like it, preserving functionality
  rowExpanded = _.noop,
  subRowHeightGetter = () => 0,
  handleCollapseClick = _.noop,

  // Header and toolbar customization
  customTableHeader,
  hideTableHeader = false,
  customToolbar,
  hideToolbar = false,
  customTableFooter,

  // Empty state messaging
  emptyResultsTitle,
  emptyResultsMessage,

  // Search and suggestions
  suggestions = {},
  userAuthorities = [],
  updateSuggestions = _.noop,
  minCharsForAsyncSuggestions = 1,
  enableLocalSearch = false,
  localSearchMatcher,

  // Export functionality
  allowExportCSV = false,
  downloadData = {},
  downloadActions = {
    fetchData: _.noop,
    abort: _.noop,
    reset: _.noop,
  },

  // Saved searches
  enableSavedSearches = false,
  savedSearches = {},
  savedSearchesActions = {
    setSelectedSavedSearch: _.noop,
    setSavedSearchDirty: _.noop,
    setSavedSearchDirtyColumns: _.noop,
    fetchSavedSearches: _.noop,
    putSavedSearch: _.noop,
    postSavedSearch: _.noop,
    deleteSavedSearch: _.noop,
  },

  // Visual and behavioral options
  highlightErrorsAndWarnings = true,
  sizes = {},
  enableOperationSelection = false,

  // Custom formatters and options
  customFormatters = {},
  customMenuOptions,

  // Bulk processing
  bulkProcessingModules = [],

  // Keyboard navigation
  onKeyboardNavigation,

  // Notifications context
  notifications = {},
}: DataTableProps<TRowData>) => {
  const initColumns = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return DataTableUtils.getAllVisibleColumns(
      browserStorage,
      savedSearches,
      entityKey,
      columns
    ) as DataTableColumn<TRowData>[]
  }

  // State initialization from constructor
  const [containerWidth, setContainerWidth] = React.useState(0)
  const [containerHeight, setContainerHeight] = React.useState(0)
  const [toolbarHeight, setToolbarHeight] = React.useState(
    hideToolbar ? 0 : TOOLBAR_DEFAULT_HEIGHT
  )
  const [allChecked, setAllChecked] = React.useState(false)
  const [columnsState, setColumnsState] = React.useState<
    DataTableColumn<TRowData>[]
  >(() => initColumns())
  const [filterColumns, setFilterColumns] = React.useState(() =>
    DataTableUtils.getSearchableColumns(columns)
  )
  const [rowDataState, setRowDataState] = React.useState<TRowData[]>(rowData)
  const [editColumnsModalOpen, setEditColumnsModalOpen] = React.useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = React.useState(false)
  const [bulkInvoked, setBulkInvoked] = React.useState(false)
  const [bulkModuleId, setBulkModuleId] = React.useState<string | null>(null)
  const [selectedRecords, setSelectedRecords] = React.useState<unknown[]>([])
  // @ts-ignore allow unused var
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isCSVDataReady, setIsCSVDataReady] = React.useState(false)
  const [activeRowIndexState, setActiveRowIndexState] = React.useState<
    number | undefined
  >(undefined)
  const [isFocused, setIsFocused] = React.useState(false)
  const [scrollLeft, setScrollLeft] = React.useState(0)

  // Refs initialization from constructor
  const searchFieldRef =
    React.useRef<React.ElementRef<typeof SearchField>>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<Table>(null)
  const scrollAnimationId = React.useRef<number | null>(null)
  const targetScrollLeft = React.useRef<number>(0)

  // Performance optimization from shouldComponentUpdate
  // This logic is now handled by the React.memo wrapper at the component export level

  /**
   * Gets the key (field or id) from a column definition
   */
  const getColumnKey = (column: DataTableColumn<TRowData>): string => {
    if ('field' in column) return column.field
    if ('id' in column) return column.id
    return ''
  }

  const setLastNonFixedColumnToFlexGrow = (
    columns: DataTableColumn<TRowData>[]
  ): DataTableColumn<TRowData>[] => {
    const lastNonFixed = _.findLast(
      columns,
      column => !(column.colProps?.fixedRight || column.colProps?.fixed)
    )

    const newColumns = columns.map(column => {
      const newCol = _.cloneDeep(column)

      if (_.isNil(newCol.colProps.flexGrow)) {
        // Handle both legacy (field) and modern (id) column formats
        // TypeScript union types require explicit type narrowing to access format-specific properties
        const isLastColumn =
          lastNonFixed &&
          (('field' in newCol &&
            'field' in lastNonFixed &&
            newCol.field === lastNonFixed.field) ||
            ('id' in newCol &&
              'id' in lastNonFixed &&
              newCol.id === lastNonFixed.id))

        newCol.colProps.flexGrow = isLastColumn ? 1 : 0
      }

      return newCol
    })

    return newColumns
  }

  const updateDirtyColumns = (columns: DataTableColumn<TRowData>[]) => {
    savedSearchesActions.setSavedSearchDirtyColumns(
      DataTableUtils.getDataColumns(columns)
    )
  }

  const saveColumns = (columns: DataTableColumn<TRowData>[]) => {
    if (!savedSearches.selectedSearchId) {
      DataTableUtils.saveDataColumnsToBrowserStorage(
        browserStorage,
        entityKey,
        columns
      )
    }

    // always update dirty columns - even if no saved search currently selected - so we can save it later if needed.
    updateDirtyColumns(columns)
  }

  hooks.useMountEffect(() => {
    if (containerRef.current) {
      // Always add keyboard listener for arrow key scrolling
      containerRef.current.addEventListener('keydown', handleKeyDown)
      containerRef.current.addEventListener('focus', handleFocus)
      containerRef.current.addEventListener('blur', handleBlur)
    }

    // Cleanup function (componentWillUnmount equivalent)
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('keydown', handleKeyDown)
        containerRef.current.removeEventListener('focus', handleFocus)
        containerRef.current.removeEventListener('blur', handleBlur)
      }

      // Cancel any ongoing scroll animation
      if (scrollAnimationId.current) {
        cancelAnimationFrame(scrollAnimationId.current)
      }
    }
  })

  // componentDidUpdate equivalent - track activeRowIndex changes
  // Only sync state when the prop changes from parent, not when internal state changes
  const prevActiveRowIndex = React.useRef(activeRowIndex)
  React.useEffect(() => {
    if (activeRowIndex !== prevActiveRowIndex.current) {
      prevActiveRowIndex.current = activeRowIndex
      setActiveRowIndexState(activeRowIndex)
    }
  }, [activeRowIndex])

  // componentDidUpdate equivalent - auto-focus for keyboard navigation
  React.useEffect(() => {
    // Auto-focus when data changes and keyboard navigation is configured
    // This ensures keyboard navigation works after pagination
    if (onKeyboardNavigation && containerRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated before focusing
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.focus()
        }
      })
    }
  }, [rowData, onKeyboardNavigation])

  // UNSAFE_componentWillUpdate equivalent - handle rowData prop changes
  // Use ref to track previous rowData to avoid effect running when selectedRecords changes
  const prevRowData = React.useRef(rowData)
  React.useEffect(() => {
    if (rowData !== prevRowData.current) {
      prevRowData.current = rowData
      // Mark records as selected based on current selectedRecords
      const updatedRowData = rowData.map(record => ({
        ...record,
        selected: selectedRecords.includes(record[primaryKeyField]),
      }))

      setRowDataState(updatedRowData)
      setAllChecked(false)
    }
  }, [rowData, selectedRecords, primaryKeyField])

  // UNSAFE_componentWillUpdate equivalent - handle columns prop changes
  // Only update when columns actually change or when a different saved search is selected
  // Using savedSearches.selectedSearchId instead of the whole object to avoid infinite loops
  const selectedSearchId = savedSearches?.selectedSearchId
  React.useEffect(() => {
    const nextCols = DataTableUtils.getAllVisibleColumns(
      browserStorage,
      savedSearches,
      entityKey,
      columns
    )

    const newCols = setLastNonFixedColumnToFlexGrow(nextCols)
    setColumnsState(newCols)
    // Don't call updateDirtyColumns here - it will be called when columns are actually modified by user actions
  }, [columns, selectedSearchId, entityKey])

  // UNSAFE_componentWillUpdate equivalent - handle savedSearches error
  const prevSavedSearchesError = React.useRef<unknown>(null)
  React.useEffect(() => {
    const currentError = savedSearches.errorInfo
    if (!prevSavedSearchesError.current && currentError) {
      // notifyError needs to be defined as a function in the functional component
      if (notifications?.toast?.open) {
        notifications.toast.open({
          // @ts-ignore error doesn't exist as a prop on the open param type, but leaving here as old version did it
          error: currentError as Error,
          title: 'Saved Search error',
        })
      }
    }
    prevSavedSearchesError.current = currentError
  }, [savedSearches.errorInfo, notifications])

  // Update filterColumns when columns prop changes to keep searchable columns in sync
  React.useEffect(() => {
    setFilterColumns(DataTableUtils.getSearchableColumns(columns))
  }, [columns])

  // Development-mode validation: Ensure bulkProcessingModules have unique IDs
  // Matches the propTypeArrayUniqueBy validation from the old class component
  React.useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      bulkProcessingModules.length > 0
    ) {
      const ids = bulkProcessingModules.map(module => module.id).filter(Boolean)
      const uniqueIds = new Set(ids)

      if (ids.length !== uniqueIds.size) {
        const duplicateCount = ids.length - uniqueIds.size
        console.warn(
          `DataTable: values must be unique. ${duplicateCount} duplicate ${
            duplicateCount === 1 ? 'value' : 'values'
          } found.`
        )
      }
    }
  }, [bulkProcessingModules])

  /**
   * Updates column properties (e.g., width, sort settings)
   * This is a little tricky... we have to do the following here:
   *
   * 1) find column to update (columnKey uses field (name))
   * 2) create new column by updating width inside colProps
   * 3) create new columns using immutability-helper to splice in new column
   *
   * ... all without mutating original state of columns
   */
  const updateColumnProps = (
    newColumnProps: Record<string, unknown>,
    columnKey: string
  ) => {
    // For legacy columns, use field; for modern columns, use id
    const index = _.findIndex(
      columnsState,
      col => getColumnKey(col) === columnKey
    )

    if (index !== -1) {
      const columnToUpdate = columnsState[index]
      const newColumn = {
        ...columnToUpdate,
        colProps: {
          ...columnToUpdate.colProps,
          ...newColumnProps,
        },
      } as DataTableColumn<TRowData> // TODO: fix this to not need the assertion, something in the props should be more strict

      const updatedColumns = update(columnsState, {
        $splice: [[index, 1, newColumn]],
      })
      setColumnsState(updatedColumns)

      // Call saveColumns with the new columns value to avoid stale closure
      saveColumns(updatedColumns)
    }
  }

  const onColumnReorderEndCallback = (event: {
    reorderColumn: string
    columnAfter?: string | null
  }) => {
    const reorderColumn = _.find(columnsState, { field: event.reorderColumn })
    // TODO: may have to consider case where reorderColumn is not in existing columns (???)
    if (reorderColumn) {
      savedSearchesActions.setSavedSearchDirty(true)
      const newColumns = _.filter(columnsState, column => {
        // Handle both legacy (field) and modern (id) formats
        if ('field' in column) {
          return column.field !== event.reorderColumn
        }
        return true
      })

      if (event.columnAfter) {
        const index = _.findIndex(newColumns, { field: event.columnAfter })
        newColumns.splice(index, 0, reorderColumn)
      } else {
        newColumns.splice(columnsState.length - 1, 0, reorderColumn)
      }

      const updatedColumns = setLastNonFixedColumnToFlexGrow(newColumns)
      setColumnsState(updatedColumns)
      saveColumns(updatedColumns)
    }
  }

  const onColumnResizeEndCallback = (
    newColumnWidth: number,
    columnKey: string
  ) => {
    savedSearchesActions.setSavedSearchDirty(true)
    updateColumnProps({ width: newColumnWidth }, columnKey)
  }

  const onColumnSort = _.debounce(
    async (columnKey: string, sortAscending: boolean, dataType: string) => {
      savedSearchesActions.setSavedSearchDirty(true)

      // Find the column definition - check both legacy (field) and modern (id) formats
      const column = _.find(
        columnsState,
        col => getColumnKey(col) === columnKey
      )

      // Extract sortField if it exists (only on legacy columns)
      const sortField =
        column && 'sortField' in column ? column.sortField : undefined

      if (changeSortBy) {
        // changeSortBy takes a sortValues array of objects with field, dataType, and sortAscending values
        await changeSortBy(
          [
            {
              field: columnKey,
              sortField,
              sortAscending,
              dataType,
            },
          ],
          columnsState
        )
        setSelectedRecords([])
        setIsCSVDataReady(false)
      } else {
        // Client-side sort
        const sortedRowData = _.orderBy(
          rowDataState,
          columnKey,
          !sortAscending ? 'desc' : 'asc'
        )
        setRowDataState(sortedRowData)
        setSelectedRecords([])
        setIsCSVDataReady(false)
      }

      // Reset previous sorted column (for single-field sort only)
      const prevSortedIndex = _.findIndex(columnsState, {
        colProps: { sort: 1 },
      })
      if (prevSortedIndex !== -1) {
        const prevColumn = columnsState[prevSortedIndex]
        // Extract the column key - handle both legacy (field) and modern (id) formats
        const prevColumnKey = getColumnKey(prevColumn)
        updateColumnProps({ sort: 0, sortAscending: undefined }, prevColumnKey)
      }

      // Update current sorted column
      updateColumnProps({ sort: 1, sortAscending }, columnKey)
    },
    DEBOUNCE_WAIT_FOR_SORT
  )

  /**
   * Toggles all checkboxes. Assumes the checkbox field is called 'selected'
   */
  const toggleAll = async () => {
    const tableMax = totalCount || 0
    if (
      rowDataState.length === DEFAULT_PRIMARY_BATCH_SIZE &&
      rowDataState.length < tableMax
    ) {
      await fetchDataRowsAppend()
    }
    // We destructure state here because rowDataState might be different if the fetch call above happens
    const currentRowData = rowDataState
    // Create new array with selected property set, maintaining immutability
    const updatedRowData = currentRowData.map(item => ({
      ...item,
      selected: !allChecked,
    }))
    const ids = updatedRowData
      .filter(item => !item.error)
      .map(item => item[primaryKeyField])

    setAllChecked(!allChecked)
    setSelectedRecords(!allChecked ? ids : [])
    setRowDataState(updatedRowData)
  }

  /**
   * Toggles a boolean field on a specific row
   * If the field is 'selected', also updates the selectedRecords array
   */
  const toggle = (rowIndex: number, field: string) => {
    // Create new row data with the toggled field (immutable update)
    const updatedRowData = rowDataState.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [field]: !item[field as keyof TRowData],
        }
      }
      return item
    })

    // Update selected records if toggling the 'selected' field
    let updatedSelectedRecords = selectedRecords
    if (field === 'selected') {
      const id = rowDataState[rowIndex][primaryKeyField]
      if (selectedRecords.includes(id)) {
        // Remove the id (immutable)
        updatedSelectedRecords = selectedRecords.filter(item => item !== id)
      } else {
        // Add the id (immutable)
        updatedSelectedRecords = [...selectedRecords, id]
      }
    }

    setRowDataState(updatedRowData)
    setSelectedRecords(updatedSelectedRecords)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  // Store the latest onKeyboardNavigation callbacks in a ref to avoid stale closures
  const onKeyboardNavigationRef = React.useRef(onKeyboardNavigation)
  React.useEffect(() => {
    onKeyboardNavigationRef.current = onKeyboardNavigation
  }, [onKeyboardNavigation])

  /**
   * Handles keyboard navigation within the table
   * Supports arrow keys for up/down navigation and left/right scrolling
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    // Ignore keyboard events that originate from editable form controls
    // (text filters, search field, etc.). Otherwise arrow keys get captured
    // and preventDefault()'d, preventing caret movement inside the field.
    const target = event.target as HTMLElement | null
    if (target) {
      const tagName = target.tagName
      const isEditable =
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT' ||
        target.isContentEditable
      if (isEditable) return
    }

    // Handle arrow keys
    switch (event.key) {
      case 'ArrowUp':
        if (onKeyboardNavigationRef.current) {
          event.preventDefault() // Prevent page scroll
          onKeyboardNavigationRef.current.gotoPreviousItem()
        }
        break
      case 'ArrowDown':
        if (onKeyboardNavigationRef.current) {
          event.preventDefault() // Prevent page scroll
          onKeyboardNavigationRef.current.gotoNextItem()
        }
        break
      case 'ArrowLeft':
        event.preventDefault() // Prevent default behavior
        scrollHorizontally(-100) // Scroll left by 100 pixels
        break
      case 'ArrowRight':
        event.preventDefault() // Prevent default behavior
        scrollHorizontally(100) // Scroll right by 100 pixels
        break
      default:
        // Do nothing for other keys
        break
    }
  }

  const handleHorizontalScroll = (scrollX: number): boolean => {
    setScrollLeft(scrollX)
    targetScrollLeft.current = scrollX // Sync ref when user manually scrolls
    return true
  }

  /**
   * Scrolls the table horizontally with smooth animation
   * @param delta - The amount to scroll in pixels (positive for right, negative for left)
   */
  const scrollHorizontally = (delta: number) => {
    // Cancel any existing animation
    if (scrollAnimationId.current) {
      cancelAnimationFrame(scrollAnimationId.current)
    }

    const startTime = Date.now()
    // Use targetScrollLeft ref as starting point to accumulate properly
    const startScroll = targetScrollLeft.current
    const targetScroll = Math.max(0, startScroll + delta)
    targetScrollLeft.current = targetScroll // Store target for next animation
    const duration = 200 // milliseconds for smooth animation

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function (ease-out) for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3)

      const currentScroll = startScroll + (targetScroll - startScroll) * easeOut
      setScrollLeft(currentScroll)

      if (progress < 1) {
        scrollAnimationId.current = requestAnimationFrame(animate)
      } else {
        scrollAnimationId.current = null
      }
    }

    requestAnimationFrame(animate)
  }

  /**
   * Handles row click events
   * Ignores clicks on action elements (links, buttons) and updates active row state
   */
  const handleRowClick = (
    event: React.SyntheticEvent<Table>,
    rowIndex: number
  ) => {
    const target = event.target as HTMLElement
    const isActionClick =
      target.onclick || target.parentElement?.nodeName === 'A'

    // Don't process row click if it's an action click
    if (isActionClick) return

    // Call the prop callback if provided
    if (onRowClick) {
      // Pass filtered row data as 4th param so consumers get correct data after local search
      onRowClick(
        event as unknown as React.MouseEvent,
        rowIndex,
        rowIndex,
        rowDataState[rowIndex]
      )
    }

    // Update internal state
    setActiveRowIndexState(rowIndex)
  }

  /**
   * Handles row double-click events.
   * Mirrors handleRowClick: ignores double-clicks that originate on action
   * elements (links, buttons) so row-level and cell-level actions don't collide.
   */
  const handleRowDoubleClick = (
    event: React.SyntheticEvent<Table>,
    rowIndex: number
  ) => {
    if (!onRowDoubleClick) return

    const target = event.target as HTMLElement
    const isActionClick =
      target.onclick || target.parentElement?.nodeName === 'A'

    if (isActionClick) return

    onRowDoubleClick(
      event as unknown as React.MouseEvent,
      rowIndex,
      rowIndex,
      rowDataState[rowIndex]
    )
  }

  /**
   * Returns CSS class name for a row based on its state
   * Handles active, error, and warning states
   */
  const rowClassNameGetter = (rowIndex: number | null | undefined): string => {
    if (_.isNil(rowIndex)) return ''

    let className = ''

    if (rowIndex === activeRowIndexState) {
      className = 'active'
    } else if (highlightErrorsAndWarnings && rowDataState[rowIndex]) {
      if (rowDataState[rowIndex].error) {
        className = 'error'
      } else if (rowDataState[rowIndex].warning) {
        className = 'warning'
      }
    }

    return className
  }

  /**
   * Returns a render function for standard column headers
   */
  const renderStandardHeader = (definition: DataTableColumn<TRowData>) => () =>
    <HeaderCell definition={definition} onSortChange={onColumnSort} />

  /**
   * Returns a render function for text cells
   */
  const renderTextCell =
    (defaultCellProps: {
      data: TRowData[]
      definition: DataTableColumn<TRowData>
      customFormatters?: Record<string, (value: unknown) => string>
    }) =>
    () =>
      (
        // @ts-ignore TextCell is a JS file with PropTypes that expects legacy column format, but we pass union type
        <TextCell {...defaultCellProps} />
      )

  /**
   * Returns header and cell render functions for checkbox columns
   * Used for bulk selection functionality
   */
  const renderCheckboxColumn = (
    definition: DataTableColumn<TRowData>,
    rowData: TRowData[],
    allChecked: boolean
  ) => ({
    header: () => (
      <Cell className={cx('header', 'checkbox-cell')}>
        <CheckboxField
          checkboxLabel={'\x00'} // force semantic to pick up clicks outside checkbox but inside cell
          elmId='select_all'
          checked={allChecked}
          className={cx('checkbox-cell')}
          onChange={toggleAll}
        />
      </Cell>
    ),
    cell: () => (
      <CheckboxCell data={rowData} definition={definition} toggle={toggle} />
    ),
  })

  /**
   * Returns the appropriate cell component based on column definition type
   * Supports custom cells and 11 built-in cell types
   */
  const getCell = (
    definition: DataTableColumn<TRowData>,
    rowData: TRowData[]
  ) => {
    if (definition.Cell) {
      // Enhance custom cell component so we can render correctly later on
      // @ts-ignore $$custom doesn't exist on string
      definition.Cell.$$custom = true
      return definition.Cell
    }

    let cell
    const defaultCellProps: {
      data: TRowData[]
      definition: DataTableColumn<TRowData>
    } = {
      data: rowData,
      definition,
    }

    switch (definition.type) {
      case COLUMN_TYPES.STATUS:
        cell = () => <StatusCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.LABEL:
        cell = () => <LabelCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.HTML:
        cell = () => <HtmlCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.BOOL:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <BoolCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.NUMBER:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <NumberCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.CURRENCY:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <CurrencyCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.DATE:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <DateCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.FILTER:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <FilterCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.TIME:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <TimeCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.VALUE_TEXT:
        // @ts-ignore need to fix prop types for the cells, and make them explicit
        cell = () => <ValueToTextCell {...defaultCellProps} />
        break
      case COLUMN_TYPES.ACTION:
        cell = () => (
          <ActionCell
            onRowAction={onRowAction}
            primaryKeyField={primaryKeyField}
            userAuthorities={userAuthorities}
            {...defaultCellProps}
          />
        )
        break
      default:
        // always include customFormatters to be used when needed
        cell = renderTextCell({ ...defaultCellProps, customFormatters })
    }
    return cell
  }

  /**
   * Renders a complete column with header and cell components
   * Handles checkbox columns, loading states, and custom cells
   */
  const renderColumn = (
    definition: DataTableColumn<TRowData>,
    rowData: TRowData[],
    allChecked: boolean,
    isLoading: boolean,
    columnIndex: number
  ) => {
    // Both header and cell are always functions in this context (from renderCheckboxColumn, renderStandardHeader, or getCell)
    let header: (props: {
      isSortableColumn: boolean
      onSortChange: typeof onColumnSort
      definition: DataTableColumn<TRowData>
    }) => React.ReactElement
    let cell: () => React.ReactElement

    const defaultCellProps = {
      data: rowData,
      definition,
    }

    if (definition.type === COLUMN_TYPES.CHECKBOX) {
      ;({ header, cell } = renderCheckboxColumn(
        definition,
        rowData,
        allChecked
      ))
    } else {
      /**
       * Typical columns go here that use default column header, but perhaps a different cell type
       */
      // @ts-ignore TODO: look into this later
      header = definition.Header || renderStandardHeader(definition)
      // @ts-ignore TODO: look into this later
      cell =
        isLoading &&
        definition.type !== COLUMN_TYPES.ACTION &&
        !isNonPrimaryBatchLoading
          ? () => <PlaceholderCell {...defaultCellProps} />
          : getCell(definition, rowData)
    }

    // NOTE: `id` should be used now, with `field` currently being used by morpheus apps. The index is a final
    // fallback to ensure unique values within the list, which keeps React happy.
    //
    // NOTE: What we call `field` here is what morpheus apps have modelled as `apiKey` in a lot of places. If you see
    // `apiKey` then it means `field` -- got it? Reasoning there is that initially it was all api keys with everything
    // mapped directly. As the apps progressed it quickly became clear that naming wasn't accurate in all cases because
    // a lot of data required modification before being placed in a cell.
    const columnKey = getColumnKey(definition) || columnIndex

    // Extract accessor with proper type safety
    const accessor: ((row: TRowData) => unknown) | undefined =
      'accessor' in definition ? definition.accessor : undefined

    // At this point, cell is always a function (from renderCheckboxColumn, getCell, or PlaceholderCell)
    // Cast to function type since FunctionalElement union is too broad here
    const cellFn = cell as (props: unknown) => string | React.ReactElement

    // Check if it's a custom cell that needs row state/actions injected
    const isCustomCell = '$$custom' in cellFn && cellFn.$$custom

    // For custom cells, wrap the cell function to inject row state/actions
    // For non-custom cells, use the cell function as-is
    const finalCell = !isCustomCell
      ? cell
      : () => (props: ColumnCellProps) => {
          const row = rowData[props.rowIndex]

          const rowState = {
            isCollapsed: collapsedRows.has(props.rowIndex),
          }

          const rowActions = {
            toggleRowCollapse: handleCollapseClick,
          }

          return cellFn({
            ...props,
            rowState,
            rowActions,
            value: accessor?.(row),
          })
        }

    // Backwards compat!!! Since we never exposed cells before older defs will work and newer ones will work better
    // This `baseField` is odd but was how morpheus was done originally and we need to support
    return (
      // @ts-ignore not sure if worth fixing this right now, TODO: circle back
      <Column
        key={columnKey}
        columnKey={columnKey}
        minWidth={40}
        header={header({
          isSortableColumn: DataTableUtils.isColumnSortable(definition),
          onSortChange: onColumnSort,
          definition,
        })}
        cell={finalCell()}
        {...definition.colProps}
      />
    )
  }

  /**
   * Handles column visibility changes from the edit columns modal
   * Along with update to the visibility, also reorder the columns the way they were added or removed in the modal.
   * Since columnsState maintains the current colProps, hence should be the main reference.
   */
  const handleColumnsVisibility = (selectedColumns: string[]) => {
    // TODO: DataTableUtils is JavaScript without types - convert to TypeScript, should be able to remove the cast once that's done
    const nonDataColumns: DataTableColumn<TRowData>[] =
      DataTableUtils.getNonDataColumns(columns) as DataTableColumn<TRowData>[]
    const reorderedDataColumns = selectedColumns.reduce<
      DataTableColumn<TRowData>[]
    >((newColumns, selectedColumn) => {
      const columnFoundInState = columnsState.find(
        column => 'field' in column && column.field === selectedColumn
      )
      if (!_.isEmpty(columnFoundInState)) {
        // column found in columnsState always visible, hence simply add to array
        newColumns.push(columnFoundInState)
      } else {
        const columnFoundInProps = columns.find(
          column => 'field' in column && column.field === selectedColumn
        )
        if (!_.isEmpty(columnFoundInProps)) {
          // column found in props columns MUST RESET its sort properties if defined as sortable
          // since we simply switch visibility here, no sort trigger
          // TODO: DataTableUtils is JavaScript without types - convert to TypeScript
          newColumns.push(
            DataTableUtils.resetSortProps(columnFoundInProps, {
              isVisible: true,
            }) as DataTableColumn<TRowData>
          )
        }
      }
      return newColumns
    }, [])

    const updatedColumns = setLastNonFixedColumnToFlexGrow([
      ...nonDataColumns,
      ...reorderedDataColumns,
    ])

    setColumnsState(updatedColumns)
    saveColumns(updatedColumns)
  }

  const resetColumns = () => {
    if (browserStorage && entityKey) {
      browserStorage.removeValue?.(`${COLUMN_DEFS_PREFIX}${entityKey}`)
    }
    const newColumns = DataTableUtils.getAllVisibleColumns(
      browserStorage,
      savedSearches,
      entityKey,
      columns
    )
    setColumnsState(newColumns)
    saveColumns(newColumns)
  }

  const renderEditColumnsModal = () => (
    /** allColumns: gives the original column configuration (should not come from any browser storage),
     * selectedColumns: gives the current visible columns (includes the fixed and visible
     * columns and may come from browser storage)
     **/
    <EditDataColumnsModal
      modalOpen={editColumnsModalOpen}
      closeModal={() => setEditColumnsModalOpen(false)}
      saveConfig={onSaveConfig}
      allColumns={columns}
      selectedColumns={columnsState}
      reset={resetColumns}
    />
  )

  const onSaveConfig = (selectedColumns: string[]) => {
    savedSearchesActions.setSavedSearchDirty(true)
    handleColumnsVisibility(selectedColumns)
    setEditColumnsModalOpen(false)
  }

  const renderDownloadModal = () => (
    <DownloadDataModal
      modalOpen={downloadModalOpen}
      closeModal={() => setDownloadModalOpen(false)}
      columns={columnsState}
      totalCount={totalCount}
      filename={`${entityKey.toLowerCase()}.csv`}
      notify={notify}
      downloadData={downloadData}
      downloadActions={downloadActions}
    />
  )

  const notify = (message: string) => {
    if (notifications.toast) {
      notifications.toast.open({ title: '', message })
    }
  }

  const handleActionSelection = (
    event: React.SyntheticEvent,
    data: DropdownItemProps
  ) => {
    const value = data.value
    if (typeof value !== 'string') return

    switch (value) {
      case EditDataColumnsMenuItem.value:
        event.nativeEvent.stopImmediatePropagation()
        setEditColumnsModalOpen(true)
        break
      case DownloadMenuItem.value:
        event.nativeEvent.stopImmediatePropagation()
        setDownloadModalOpen(true)
        break
      default:
        break
    }
  }

  const isDuplicateClause = (
    filterClause: FilterClause
  ): string | undefined => {
    return _.findKey(filterClauses, (clause: FilterClause) => {
      return (
        _.isEqual(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          clause.values.map(_.toLower).sort(),
          filterClause.values.map(_.toLower).sort()
        ) && _.isEqual(clause.fields, filterClause.fields)
      )
    })
  }

  const removeFilterFunc = (filterClause: FilterClause) => {
    setIsCSVDataReady(false)
    removeFilter(filterClause)
    updateDirtyColumns(columnsState)
  }

  const onSearchFieldSubmit = (filterClause: FilterClause) => {
    const existingClauseIndex = DataTableUtils.getClauseIndex(
      filterClauses,
      filterClause
    )
    // We don't use _.compact here because we want to keep false values
    // @ts-ignore NaN isn't assignable to a string but preserving previous treatment here even if typescript doesn't like it
    if (_.isEmpty(_.without(filterClause.values, undefined, null, '', NaN))) {
      removeFilterFunc(filterClause)
      setSelectedRecords([])
      setIsCSVDataReady(false)
      if (existingClauseIndex !== -1) {
        notify(`${filterClause.title} filter removed`)
      }
    } else {
      if (!isDuplicateClause(filterClause)) {
        addFilter(filterClause)
        updateDirtyColumns(columnsState)
        notify(
          `${filterClause.title} filter ${
            existingClauseIndex === -1 ? 'added' : 'updated'
          }`
        )
        setSelectedRecords([])
        setIsCSVDataReady(false)
      } else {
        notify(
          `Filter [${filterClause.title}: ${filterClause.valuesAsText}] already applied`
        )
      }
    }
  }

  const resizeContainer = (width: number, height: number) => {
    setContainerWidth(width)
    setContainerHeight(height)
  }

  const resizeToolbar = (_width: number, height: number) => {
    setToolbarHeight(hideToolbar ? 0 : height || TOOLBAR_DEFAULT_HEIGHT)
  }

  const onSelectSavedSearch = (id: string | null) => {
    savedSearchesActions.setSelectedSavedSearch(id || null)
    if (searchFieldRef.current) {
      searchFieldRef.current.updateColumn(null, { value: '' }) // clear currently selected field in searchField
    }
    _.defer(refreshData)
  }

  const onCreateSavedSearch = async (
    searchName: string,
    savedSearchType: string
  ) => {
    await savedSearchesActions.postSavedSearch(searchName, savedSearchType)
  }

  const onUpdateSavedSearch = async () => {
    await savedSearchesActions.putSavedSearch()
  }

  const onDeleteSavedSearch = async (id: string) => {
    // first, unselect whatever saved search is selected
    if (savedSearches.selectedSearchId) {
      onSelectSavedSearch(null)
    }
    // then blast it
    await savedSearchesActions.deleteSavedSearch(id)
  }

  const searchNestedValues = (
    obj: Record<string, unknown>,
    searchFilter: string
  ): boolean => {
    const regex = searchRegExp(searchFilter)
    return Object.values(obj).some(val => {
      if (typeof val === 'object' && val !== null) {
        // If the value is a nested object, search its values recursively
        return searchNestedValues(val as Record<string, unknown>, searchFilter)
      } else {
        // If the value is not an object, test it against the search filter
        return regex.test((val || '').toString())
      }
    })
  }

  const onLocalSearchChange = (
    _event: React.MouseEvent<HTMLElement>,
    data: { value?: string }
  ) => {
    const searchFilter = data.value || ''
    // Use rowData prop as the source for local search filtering
    // This ensures we're always searching against the original dataset
    // this means local search wouldn't key off of selected: true/false but that's not really an expected functionality
    // or something the old version had, just a callout about the way this is working currently
    const currentRowData = rowData

    const filteredData = !searchFilter
      ? currentRowData
      : currentRowData.filter(row =>
          localSearchMatcher
            ? localSearchMatcher(row, searchFilter)
            : searchNestedValues(row as Record<string, unknown>, searchFilter)
        )

    // Restore selection state on filtered data
    // Use Set for O(1) lookup instead of O(m) array.includes()
    const selectedSet = new Set(selectedRecords)
    const dataWithSelections = filteredData.map(record => ({
      ...record,
      selected: selectedSet.has(record[primaryKeyField]),
    }))

    setRowDataState(dataWithSelections)
  }

  const renderStandardSearch = () => {
    return (
      <SearchField
        ref={searchFieldRef}
        filterColumns={filterColumns}
        suggestions={suggestions}
        updateSuggestions={updateSuggestions}
        onSubmit={onSearchFieldSubmit}
        minCharsForAsyncSuggestions={minCharsForAsyncSuggestions}
        clauses={filterClauses}
        enableOperationSelection={enableOperationSelection}
      />
    )
  }

  const renderLocalSearch = () => (
    <CoreSearchField
      elmId={`${entityKey}_local_search`}
      className={cx('local-search')}
      placeholder='Search'
      onSearchChange={onLocalSearchChange}
      showNoResults={false}
      deleteIconEnabled
      compact
      hideWrapper
    />
  )

  const renderSavedSearch = () => (
    <SavedSearches
      selectedSearch={_.get(savedSearches, 'selectedSearchId')}
      isDirty={_.get(savedSearches, 'isDirty')}
      searches={_.get(savedSearches, 'searches', [])}
      onCreate={onCreateSavedSearch}
      onUpdate={onUpdateSavedSearch}
      onDelete={onDeleteSavedSearch}
      onSelect={onSelectSavedSearch}
      isUserGlobalSearchManager={userAuthorities.includes('GSW')}
    />
  )

  const renderDataTableActions = () => {
    const options: CustomMenuOption[] = [EditDataColumnsMenuItem]

    if (allowExportCSV) {
      // Clone DownloadMenuItem to avoid mutating the shared constant
      const downloadOption: CustomMenuOption = {
        ...DownloadMenuItem,
        isDisabled: !totalCount,
      }
      options.unshift(downloadOption)
    }

    if (customMenuOptions && customMenuOptions.length > 0) {
      customMenuOptions.forEach(option => options.push(option))
    }

    return (
      <>
        <ActionIcon
          elmId='refresh'
          onClick={refreshData}
          name='refresh'
          loading={isLoading}
        />
        <Dropdown
          className={cx('right-dropdown')}
          icon={null}
          trigger={<ActionIcon name='ellipsis vertical' />}
          selectOnBlur={false}
          data-elm-id='dropdown_table_actions'
          position='right'
          closeOnChange
        >
          <Dropdown.Menu>
            {options.map((option, index) => (
              <Dropdown.Item
                text={option.text}
                value={option.value}
                key={index}
                onClick={option.clickHandler || handleActionSelection}
                disabled={option.isDisabled}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </>
    )
  }

  const renderToolbar = () => {
    return !hideToolbar ? (
      <div className={cx('toolbar')}>
        {renderStandardSearch()}
        {enableLocalSearch && renderLocalSearch()}
        {enableSavedSearches && renderSavedSearch()}
        {renderDataTableActions()}
      </div>
    ) : null
  }

  const renderTableHeaderInfo = (rowData: TRowData[]) => {
    const anyCheckboxColumn = _.some(columnsState, {
      type: COLUMN_TYPES.CHECKBOX,
    })
    const countSelected = _.sumBy(rowData, row => (row.selected ? 1 : 0))
    return (
      <div className={cx('info')}>
        {anyCheckboxColumn && (
          <span>
            <strong data-elm-id='count_selected'>
              {/* @ts-ignore should probably cast countSelected to string but this has worked in the past so dont want to change functionality much */}
              {formatNumber(countSelected)}
            </strong>
            {' of '}
          </span>
        )}
        <strong data-elm-id='total_count_client_side'>
          {/* @ts-ignore should probably cast countSelected to string but this has worked in the past so dont want to change functionality much */}
          {formatNumber(totalCount || rowData.length)}
        </strong>
        {anyCheckboxColumn
          ? ' Selected'
          : ` ${_.startCase(_.lowerCase(entityKey))}`}
      </div>
    )
  }

  const invokeBulkAction = (
    bulkModuleId: string,
    selectedRecords: unknown[]
  ) => {
    setBulkInvoked(true)
    setBulkModuleId(bulkModuleId)
    setSelectedRecords(selectedRecords)
  }

  const completeBulkAction = () => {
    setBulkInvoked(false)
    setBulkModuleId(null)
  }

  const clearFiltersFunc = () => {
    setIsCSVDataReady(false)
    clearFilters()
    savedSearchesActions.setSelectedSavedSearch(null)

    _.defer(refreshData)
  }

  const triggerButton = (
    <div data-elm-id='bulk_action_button' className={cx('bulk-trigger')}>
      <Icon
        name='write square'
        size='large'
        data-elm-id='bulk_action_icon'
        color='info'
      />
      <label className={cx('bulk-label')}>Bulk</label>
    </div>
  )

  const renderTableHeader = (rowData: TRowData[]) => {
    const selectedIds = _.map(
      rowData.filter(row => row.selected),
      row => row[primaryKeyField]
    )
    const countSelected = _.size(selectedIds)

    const bulkMenu = !!bulkProcessingModules.length && (
      <div className={cx('bulk-option')}>
        <Dropdown
          disabled={countSelected === 0}
          icon={null}
          trigger={triggerButton}
        >
          <Dropdown.Menu>
            <Dropdown.Header
              data-elm-id='bulk_menu_dropdown_header'
              // @ts-ignore formatNumber type isn't right but it's a js file
              content={`${formatNumber(countSelected)} ${pluralize(
                String(entityName),
                countSelected
              )} Selected`}
            />
            {countSelected > 0 &&
              bulkProcessingModules.map(module => (
                <Dropdown.Item
                  key={module.id}
                  data-elm-id={`bulk_menu_item_${_.snakeCase(module.id)}`}
                  onClick={() => invokeBulkAction(module.id, selectedIds)}
                >
                  {module.menuOptionTitle}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    )

    return !hideTableHeader ? (
      <div className={cx('data-table-header')}>
        {bulkMenu}
        <div className={cx('content')}>
          {!customTableHeader
            ? renderTableHeaderInfo(rowData)
            : customTableHeader()}
          <ClauseHolder
            className={cx('clauses')}
            clauses={filterClauses}
            clearFilters={clearFiltersFunc}
            removeFilter={removeFilterFunc}
            onFilterClick={
              searchFieldRef.current
                ? searchFieldRef.current.updateColumn
                : _.noop
            }
          />
        </div>
      </div>
    ) : null
  }

  const footerHeight = sizes.footerHeight || 0
  // NOTE: This forces all height calculations to be defined by the caller and we occupy the space.
  const tableHeight =
    containerHeight -
    ((hideTableHeader ? 0 : INFOBAR_DEFAULT_HEIGHT) + toolbarHeight) -
    footerHeight

  const bulkProcessingComponent = (props: {
    selectedRecords: unknown[]
    invoked: boolean
    onComplete: () => void
  }) => {
    if (!bulkProcessingModules.length) return null

    const module = bulkProcessingModules.find(m => m.id === bulkModuleId)

    if (!module || !module.component) return null
    return module.component(props)
  }

  const standardToolbarProps = {
    allowExportCSV,
    refreshData,
    isLoading,
    enableLocalSearch,
    enableSavedSearches,
    savedSearches,
    filterClauses,
    userAuthorities,
    renderStandardSearch,
    renderLocalSearch,
    renderSavedSearch,
    renderDataTableActions,
  }

  return (
    <div
      ref={containerRef}
      className={cx('data-table-container', {
        focused: isFocused,
      })}
      data-entity-key={entityKey}
      tabIndex={0}
    >
      <ReactResizeDetector
        handleWidth
        handleHeight
        skipOnMount
        onResize={resizeContainer}
      />
      {renderEditColumnsModal()}
      {renderDownloadModal()}
      <div>
        <ReactResizeDetector
          handleWidth
          handleHeight
          skipOnMount
          onResize={resizeToolbar}
        />
        {customToolbar ? customToolbar(standardToolbarProps) : renderToolbar()}
      </div>
      {renderTableHeader(rowDataState)}

      <Table
        ref={tableRef}
        rowsCount={rowDataState ? rowDataState.length : 0}
        rowHeight={sizes.rowHeight || DEFAULT_ROW_HEIGHT}
        headerHeight={sizes.headerHeight || DEFAULT_HEADER_HEIGHT}
        width={containerWidth}
        height={
          !isLoading &&
          rowDataState.length === 0 &&
          (emptyResultsTitle || emptyResultsMessage)
            ? (sizes.headerHeight || DEFAULT_HEADER_HEIGHT) + 2 // header-only height when showing empty state
            : tableHeight
        }
        onColumnResizeEndCallback={onColumnResizeEndCallback}
        onScrollEnd={onScrollEnd}
        onHorizontalScroll={handleHorizontalScroll}
        isColumnResizing={false}
        onColumnReorderEndCallback={onColumnReorderEndCallback}
        isColumnReordering={false}
        // @ts-ignore Table component doesn't have rowAttributesGetter in types but it exists in fixed-data-table-2
        rowAttributesGetter={rowAttributesGetter}
        rowClassNameGetter={rowClassNameGetter}
        className='data-table adc-datatable'
        onRowClick={handleRowClick}
        onRowDoubleClick={onRowDoubleClick ? handleRowDoubleClick : undefined}
        subRowHeightGetter={subRowHeightGetter}
        rowExpanded={rowExpanded !== _.noop ? rowExpanded : undefined}
        scrollToRow={activeRowIndex}
        scrollLeft={scrollLeft || 0}
      >
        {columnsState.map((column, columnIndex) =>
          renderColumn(column, rowDataState, allChecked, isLoading, columnIndex)
        )}
      </Table>

      {!isLoading &&
        rowDataState.length === 0 &&
        (emptyResultsTitle || emptyResultsMessage) && (
          <div
            className={cx('empty-results')}
            style={{
              height:
                tableHeight - (sizes.headerHeight || DEFAULT_HEADER_HEIGHT) - 2,
            }}
          >
            {emptyResultsTitle && <h3>{emptyResultsTitle}</h3>}
            {emptyResultsMessage && <p>{emptyResultsMessage}</p>}
          </div>
        )}

      {customTableFooter && React.createElement(customTableFooter)}

      {bulkProcessingComponent({
        selectedRecords,
        invoked: bulkInvoked,
        onComplete: completeBulkAction,
      })}
    </div>
  )
}

// Performance optimization: Memoize component and prevent re-renders during bulk operations
// This replaces the old shouldComponentUpdate logic from the class component
const MemoizedFunctionalDataTable = React.memo(
  FunctionalDataTable,
  (prevProps, nextProps) => {
    // Only allow updates when bulk is off or it's about to be turned off
    // This matches the original shouldComponentUpdate behavior
    const prevBulkInvoked = prevProps.bulkProcessingModules?.some(
      module =>
        module.id && prevProps.rowData?.some((row: any) => row.bulkInvoked)
    )
    const nextBulkInvoked = nextProps.bulkProcessingModules?.some(
      module =>
        module.id && nextProps.rowData?.some((row: any) => row.bulkInvoked)
    )

    // Prevent render when bulk stays invoked
    if (prevBulkInvoked && nextBulkInvoked) {
      return true // true means don't re-render
    }

    return false // false means do re-render
  }
) as typeof FunctionalDataTable

// Export the memoized functional component wrapped with notifications context
export default withApplicationNotificationsContext(MemoizedFunctionalDataTable)
export {
  ActionIcon,
  BulkProcessingModule,
  BulkUpdateDrawer,
  defineTableSchema,
  DownloadDataModal,
  DownloadMenuItem,
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  COLUMN_STANDARD_SIZES,
  DataTableUtils as utils,
}
export { useSavedSearches } from './hooks/useSavedSearches'
export type {
  SavedSearchProviders,
  SavedSearchesState,
  SavedSearchesActions,
  SavedSearchType,
  ParsedSavedSearch,
  UseSavedSearchesArgs,
  UseSavedSearchesResult,
} from './hooks/useSavedSearches'
export type {
  FilterAppModel,
  SavedSearchFilterClause,
} from './utils/savedSearchFilters'
export * from './types'
export * from './builtinCells'
